import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowRight, FiCheckCircle, FiShield, FiTruck, FiSettings,
  FiUsers, FiStar, FiAward, FiMessageSquare, FiPackage,
  FiClock, FiThumbsUp, FiZap, FiChevronLeft, FiChevronRight,
} from 'react-icons/fi';
import API from '../utils/api';
import useSiteSettings from '../hooks/useSiteSettings';
import './AboutPage.css';

/* ════════════════════════════════════════════════════════════════
   STATIC CONTENT
   ════════════════════════════════════════════════════════════════ */

const VALUES = [
  {
    icon: <FiShield size={24} />,
    color: '#0078BF',
    bg: 'rgba(0,120,191,0.08)',
    title: 'Quality First',
    desc: 'We never compromise on print quality. Every order goes through strict quality checks before it leaves our facility.',
  },
  {
    icon: <FiUsers size={24} />,
    color: '#16a34a',
    bg: 'rgba(22,163,74,0.08)',
    title: 'Customer Centric',
    desc: 'Our customers are at the heart of everything we do. We listen, adapt, and deliver beyond expectations every time.',
  },
  {
    icon: <FiZap size={24} />,
    color: '#7c3aed',
    bg: 'rgba(124,58,237,0.08)',
    title: 'Speed & Reliability',
    desc: 'Fast turnaround without cutting corners. We deliver on time, every time — because your deadlines matter.',
  },
  {
    icon: <FiSettings size={24} />,
    color: '#ea580c',
    bg: 'rgba(234,88,12,0.08)',
    title: 'Innovation',
    desc: 'We continuously invest in modern printing technology to offer cutting-edge solutions and finishes.',
  },
  {
    icon: <FiThumbsUp size={24} />,
    color: '#db2777',
    bg: 'rgba(219,39,119,0.08)',
    title: 'Integrity',
    desc: 'Transparent pricing, honest timelines, and no hidden charges. What you see is exactly what you get.',
  },
  {
    icon: <FiAward size={24} />,
    color: '#0891b2',
    bg: 'rgba(8,145,178,0.08)',
    title: 'Excellence',
    desc: 'A culture of continuous improvement — we raise the bar with every project we take on.',
  },
];

const HOW_STEPS = [
  {
    num: '01',
    icon: <FiMessageSquare size={22} />,
    color: '#0078BF',
    bg: '#e8f4ff',
    title: 'Understand',
    desc: 'We understand your requirement and printing needs.',
  },
  {
    num: '02',
    icon: <FiSettings size={22} />,
    color: '#16a34a',
    bg: '#dcfce7',
    title: 'Create',
    desc: 'We prepare the right design, material and production solution.',
  },
  {
    num: '03',
    icon: <FiPackage size={22} />,
    color: '#7c3aed',
    bg: '#f3e8ff',
    title: 'Produce',
    desc: 'Your order goes through careful printing and quality checks.',
  },
  {
    num: '04',
    icon: <FiTruck size={22} />,
    color: '#ea580c',
    bg: '#fff7ed',
    title: 'Deliver',
    desc: 'We make sure the finished product reaches you as expected.',
  },
];

const WHY_ITEMS = [
  {
    icon: <FiShield size={20} />,
    title: 'Premium Materials & Finishes',
    desc: 'We source only the best paper stocks, inks, and finishing materials to ensure every print looks professional.',
  },
  {
    icon: <FiClock size={20} />,
    title: 'Fast Turnaround, On-Time Delivery',
    desc: 'Most orders are completed within 24–48 hours. We ship on schedule and keep you updated every step of the way.',
  },
  {
    icon: <FiStar size={20} />,
    title: 'Dedicated Customer Support',
    desc: 'Our team is available to assist you from order placement to delivery — responsive, friendly, and solution-focused.',
  },
  {
    icon: <FiCheckCircle size={20} />,
    title: 'Competitive, Transparent Pricing',
    desc: 'Best-in-market rates with no hidden costs. Get clear quotes upfront and volume discounts for bulk orders.',
  },
];

/* ════════════════════════════════════════════════════════════════
   NUMBERS SECTION — dynamic, fetches from /api/about-stats/public
   ════════════════════════════════════════════════════════════════ */
const NumbersSection = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/about-stats/public')
      .then(({ data }) => setStats(data.stats || []))
      .catch(() => setStats([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && stats.length === 0) return null;

  return (
    <section className="about-numbers">
      <div className="container">
        <div className="about-numbers-inner">
          <div className="about-numbers-left">
            <span className="about-label">Our Impact</span>
            <h2>Numbers That Matter</h2>
            <p>
              Over the years, we've grown with our customers and built a
              reputation for quality and reliability.
            </p>
          </div>

          <div className="about-numbers-stats">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="about-stat-item">
                    <div
                      className="about-skeleton-line"
                      style={{ width: 80, height: 36, margin: '0 auto 10px', borderRadius: 6 }}
                    />
                    <div
                      className="about-skeleton-line"
                      style={{ width: 60, height: 12, margin: '0 auto', borderRadius: 4, opacity: 0.5 }}
                    />
                  </div>
                ))
              : stats.map((stat) => (
                  <div key={stat.id} className="about-stat-item">
                    {stat.icon && (
                      <div className="about-stat-icon">
                        <span style={{ fontSize: 22 }}>{stat.icon}</span>
                      </div>
                    )}
                    <div className="about-stat-value">{stat.value}</div>
                    <div className="about-stat-label">{stat.label}</div>
                  </div>
                ))
            }
          </div>
        </div>
      </div>
    </section>
  );
};

/* ════════════════════════════════════════════════════════════════
   CLIENTS SECTION — reuses same API + carousel CSS as HomePage.
   Does NOT import OurClients (which renders its own <section> +
   header). Instead we fetch the same endpoint and render only the
   carousel markup using the existing CSS classes so styles match.
   ════════════════════════════════════════════════════════════════ */
const ClientsSection = () => {
  const scrollRef = useRef(null);
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/client-logos/public')
      .then(({ data }) => setLogos(data.logos || []))
      .catch(() => setLogos([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && logos.length === 0) return null;

  const scroll = (dir) => {
    if (scrollRef.current)
      scrollRef.current.scrollBy({ left: dir * 240, behavior: 'smooth' });
  };

  return (
    <section className="about-clients">
      <div className="container">
        {/* Our own header — no duplicate from OurClients */}
        <div className="about-header-center">
          <span className="about-label">Our Clients</span>
          <h2 className="about-section-title">
            Trusted by Businesses &amp; Organizations
          </h2>
          <p className="about-section-sub">
            We are proud to work with a diverse range of clients, from small
            businesses to large organizations.
          </p>
        </div>

        {/* Carousel — reuses existing global CSS classes */}
        <div className="clients-carousel-wrapper">
          <button
            className="clients-nav-btn"
            onClick={() => scroll(-1)}
            aria-label="Previous clients"
          >
            <FiChevronLeft size={18} />
          </button>

          <div className="clients-scroll" ref={scrollRef}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="client-logo-card"
                    style={{ background: '#f3f4f6', border: '1px solid #e5e7eb' }}
                  >
                    <div style={{ width: 80, height: 24, borderRadius: 4, background: '#e5e7eb' }} />
                  </div>
                ))
              : logos.map((logo) => (
                  <div key={logo.id} className="client-logo-card">
                    <img
                      src={logo.logoUrl}
                      alt={logo.name}
                      className="client-logo-img"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ))
            }
          </div>

          <button
            className="clients-nav-btn"
            onClick={() => scroll(1)}
            aria-label="Next clients"
          >
            <FiChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ════════════════════════════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════════════════════════════ */
const AboutPage = () => {
  /* Section images — all admin-controlled via SiteSettings */
  const { data: heroSettings }  = useSiteSettings('about_us_hero');
  const { data: storySettings } = useSiteSettings('about_us_story');
  const { data: whySettings }   = useSiteSettings('about_us_why');

  const heroImage =
    heroSettings?.heroImage ||
    'https://images.unsplash.com/photo-1562577309-4932fdd64cd1?w=900&auto=format&fit=crop&q=80';
  const storyImage =
    storySettings?.image ||
    'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=700&auto=format&fit=crop&q=80';
  const whyImage =
    whySettings?.image ||
    'https://images.unsplash.com/photo-1542744094-24638eff58bb?w=700&auto=format&fit=crop&q=80';

  return (
    <>
      <Helmet>
        <title>About Us | Angel Printers</title>
        <meta
          name="description"
          content="Learn about Angel Printers — our story, values, and commitment to delivering premium printing solutions for businesses and individuals across India."
        />
      </Helmet>

      <div className="about-page">

        {/* ── 1. HERO ──────────────────────────────────────────── */}
        <section className="about-hero">
          <div className="container">
            <div className="about-hero-inner">
              {/* Left: text */}
              <div className="about-hero-text">
                <span className="about-hero-label">About Us</span>
                <h1 className="about-hero-title">
                  Turning Ideas Into{' '}
                  <span className="about-hero-highlight">
                    Something You Can Hold.
                  </span>
                </h1>
                <p className="about-hero-desc">
                  We are a team of passionate print professionals, dedicated to
                  helping businesses, brands and individuals bring their ideas to
                  life with high-quality, reliable and affordable printing
                  solutions.
                </p>
                <div className="about-hero-btns">
                  <Link to="/services" className="btn btn-primary">
                    Explore Our Services <FiArrowRight size={16} />
                  </Link>
                  <Link to="/products" className="btn btn-outline">
                    View Products <FiArrowRight size={16} />
                  </Link>
                </div>
              </div>

              {/* Right: hero image — admin-controlled */}
              <div className="about-hero-img-wrap">
                <img
                  src={heroImage}
                  alt="Angel Printers — our printing facility"
                  className="about-hero-img"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── 2. OUR STORY ─────────────────────────────────────── */}
        <section className="about-story">
          <div className="container">
            <div className="about-story-inner">
              {/* Left: image */}
              <img
                src={storyImage}
                alt="Angel Printers team at work"
                className="about-story-img"
                loading="lazy"
              />

              {/* Right: text */}
              <div className="about-story-text">
                <span className="about-label">Who We Are</span>
                <h2>Your Trusted Printing Partner</h2>
                <p>
                  Angel Printers is a modern printing company committed to
                  delivering high-quality prints with precision, speed and care.
                  We combine advanced printing technology with a customer-first
                  approach to provide customized solutions for businesses, brands
                  and individuals.
                </p>
                <p>
                  From everyday essentials to large format printing, we handle
                  every project with attention to detail and a focus on quality.
                  Whether you're a startup looking for your first business cards
                  or an enterprise running large campaigns, we scale with your
                  needs.
                </p>
                <div className="about-story-pills">
                  <span className="about-story-pill">
                    <FiShield size={15} /> Quality Focused
                  </span>
                  <span className="about-story-pill">
                    <FiTruck size={15} /> Reliable Service
                  </span>
                  <span className="about-story-pill">
                    <FiSettings size={15} /> Custom Solutions
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3. OUR VALUES ────────────────────────────────────── */}
        <section className="about-values">
          <div className="container">
            <div className="about-header-center">
              <span className="about-label">Our Values</span>
              <h2 className="about-section-title">What Drives Us Every Day</h2>
              <p className="about-section-sub">
                These are the principles that guide every decision we make and
                every print we produce.
              </p>
            </div>
            <div className="about-values-grid">
              {VALUES.map((v) => (
                <div key={v.title} className="about-value-card">
                  <div
                    className="about-value-icon"
                    style={{ background: v.bg, color: v.color }}
                  >
                    {v.icon}
                  </div>
                  <h3>{v.title}</h3>
                  <p>{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 4. HOW WE WORK ───────────────────────────────────── */}
        <section className="about-how">
          <div className="container">
            <div className="about-how-inner">
              {/* Left: heading block */}
              <div className="about-how-left">
                <span className="about-label">Our Approach</span>
                <h2>Simple Steps to Perfect Prints</h2>
                <p>
                  From your idea to the final print, we make the process easy
                  and hassle-free.
                </p>
              </div>

              {/* Right: steps */}
              <div className="about-how-steps">
                {HOW_STEPS.map((step) => (
                  <div key={step.num} className="about-how-step">
                    <div
                      className="about-how-step-num"
                      style={{ background: step.bg, color: step.color }}
                    >
                      {step.num}
                    </div>
                    <div
                      className="about-how-step-icon"
                      style={{ color: step.color }}
                    >
                      {step.icon}
                    </div>
                    <div className="about-how-step-text">
                      <h4>{step.title}</h4>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. OUR NUMBERS — dynamic ─────────────────────────── */}
        <NumbersSection />

        {/* ── 6. OUR CLIENTS — same data as Home, no duplicate ──── */}
        <ClientsSection />

        {/* ── 7. WHY CHOOSE US ─────────────────────────────────── */}
        <section className="about-why">
          <div className="container">
            <div className="about-why-inner">
              {/* Left: content */}
              <div className="about-why-content">
                <span className="about-label">Why Choose Us</span>
                <h2>The Angel Printers Difference</h2>
                <p>
                  With hundreds of printing companies to choose from, here's
                  what sets us apart and why thousands of customers keep coming
                  back.
                </p>
                <div className="about-why-list">
                  {WHY_ITEMS.map((item) => (
                    <div key={item.title} className="about-why-item">
                      <div className="about-why-item-icon">{item.icon}</div>
                      <div className="about-why-item-text">
                        <h4>{item.title}</h4>
                        <p>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: image */}
              <div className="about-why-img-wrap">
                <img
                  src={whyImage}
                  alt="Quality printing at Angel Printers"
                  className="about-why-img"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── 8. FINAL CTA ─────────────────────────────────────── */}
        <section className="about-cta">
          <div className="container">
            <div className="about-cta-inner">
              {/* Left: text */}
              <div className="about-cta-content">
                <h2>Have a Printing Requirement?</h2>
                <p>
                  Tell us what you need and we'll help you find the right
                  printing solution.
                </p>
              </div>

              {/* Right: buttons */}
              <div className="about-cta-btns">
                <Link to="/get-quote" className="btn btn-primary"
                  style={{ background: '#fff', color: 'var(--primary)' }}>
                  Get a Quote <FiArrowRight size={16} />
                </Link>
                <Link to="/products" className="btn btn-outline"
                  style={{ borderColor: 'rgba(255,255,255,0.5)', color: '#fff' }}>
                  Explore Products <FiArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default AboutPage;